import { useEffect, useRef } from 'react';
import type { Presentation } from '../../types';
import { DeepLinkCta } from './DeepLinkCta';

interface Props {
  p: Extract<Presentation, { type: 'chart' }>;
}

// Chart.js loaded dynamically so the bundle doesn't pay the chart cost on
// every page-load — only sessions that produce a chart envelope pull it in.
export function ChartPresentation({ p }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let chart: { destroy: () => void } | null = null;
    let cancelled = false;

    (async () => {
      if (!canvasRef.current || typeof window === 'undefined') return;
      try {
        const { Chart, registerables } = await import('chart.js');
        Chart.register(...registerables);
        if (cancelled || !canvasRef.current) return;
        chart = new Chart(canvasRef.current, {
          type: p.chart_type,
          data: {
            labels: p.data.labels,
            datasets: [
              {
                label: 'Count',
                data: p.data.values,
                backgroundColor: ['#005ea2', '#00a91c', '#e6a51c', '#d54309', '#71767a'],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: p.chart_type !== 'bar' } },
          },
        });
      } catch (err) {
        if (canvasRef.current?.parentElement) {
          canvasRef.current.parentElement.innerText = `Chart unavailable: ${(err as Error).message}`;
        }
      }
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [p.chart_type, p.data]);

  return (
    <div className="presentation-card">
      <div className="presentation-title">📊 Chart</div>
      <div className="chart-canvas-wrap" style={{ height: 320, position: 'relative' }}>
        <canvas ref={canvasRef} />
      </div>
      {p.cta && <DeepLinkCta cta={p.cta} />}
    </div>
  );
}
